import { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CATALOG_SNAPSHOT } from "../data/catalogSnapshot";

const card = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const ProductGrid = () => {
  const [products, setProducts] = useState(CATALOG_SNAPSHOT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { addItem, cartEnabled, minOrderQty } = useCart();
  const cancelledRef = useRef(false);

  // Recupero prodotti resiliente: retry con backoff; NON svuota mai un catalogo
  // già popolato per un errore transitorio; espone uno stato d'errore recuperabile.
  const fetchProducts = useCallback(async (retries = 3) => {
    const CACHE_KEY = "catalog_cache_v2";
    const readCache = () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        const arr = raw ? JSON.parse(raw) : null;
        return Array.isArray(arr) && arr.length > 0 ? arr : null;
      } catch {
        return null;
      }
    };
    const serveCacheOrError = () => {
      const cached = readCache();
      // Non mostrare MAI un catalogo vuoto: cache locale, altrimenti snapshot statico.
      setProducts(cached || CATALOG_SNAPSHOT);
      setError(false);
      setLoading(false);
    };
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const data = await getProducts();
        if (cancelledRef.current) return;
        if (Array.isArray(data) && data.length > 0) {
          setProducts(data);
          setError(false);
          setLoading(false);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
          return;
        }
        if (attempt === retries) {
          serveCacheOrError();
          return;
        }
      } catch (e) {
        if (cancelledRef.current) return;
        if (attempt === retries) {
          serveCacheOrError();
          return;
        }
      }
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }, []);

  const retry = () => {
    setError(false);
    setLoading(true);
    fetchProducts();
  };

  useEffect(() => {
    cancelledRef.current = false;
    fetchProducts();

    // Self-heal: se la scheda torna attiva/visibile, ricarica il catalogo
    const onWake = () => {
      if (document.visibilityState === "visible") fetchProducts();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      cancelledRef.current = true;
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [fetchProducts]);

  const handleAdd = (p) => {
    addItem(p);
    toast.success(`${p.name} aggiunta al carrello`);
  };

  return (
    <section id="catalogo" className="py-24 md:py-32 bg-[#0D110D]" data-testid="catalog-section">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-4">
              Il Catalogo
            </p>
            <h2 className="font-serif-display text-5xl md:text-7xl tracking-tight text-[#E8E2D2] leading-none">
              Le Nostre <span className="italic">Birre</span>
            </h2>
          </div>
          <p className="font-mono-accent text-xs text-[#E8E2D2]/40 uppercase tracking-widest">
            {products.length > 0 ? `${products.length} referenze` : ""}
          </p>
        </div>

        <div className="flex items-center gap-3 border border-[#D47738]/40 bg-[#D47738]/10 px-5 py-4 mb-12" data-testid="min-order-banner">
          <Plus className="w-4 h-4 text-[#D47738] shrink-0" strokeWidth={2.5} />
          <p className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#E8E2D2]/80 leading-relaxed">
            Confezione minima: <span className="text-[#D47738]">{Number(minOrderQty) || 12} lattine</span> per ordine. Puoi mixare liberamente le tre birre.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-[#0D110D] aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : error && products.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-24 border border-white/10 bg-[#141A14]" data-testid="catalog-error">
            <p className="font-serif-display text-2xl md:text-3xl text-[#E8E2D2] mb-3">
              Non riusciamo a caricare le birre
            </p>
            <p className="text-sm text-[#E8E2D2]/50 max-w-md mb-8 leading-relaxed">
              C'è stato un problema temporaneo nel recuperare il catalogo. Riprova tra un istante.
            </p>
            <button
              onClick={retry}
              className="inline-flex items-center gap-2 bg-[#D47738] text-[#0D110D] px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300"
              data-testid="catalog-retry-btn"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={2} /> Riprova
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {products.map((p, i) => (
              <motion.article
                key={p.id}
                custom={i}
                variants={card}
                initial={false}
                animate="visible"
                className="group bg-[#0D110D] relative"
                data-testid={`product-card-${i}`}
              >
                <Link to={`/birra/${p.id}`} className="block relative overflow-hidden aspect-[3/4] bg-[#141A14]" data-testid={`product-link-${i}`}>
                  <img
                    src={p.image}
                    alt={`${p.name} — ${p.style}, Birrificio Vecchio Cinghiale`}
                    className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-105"
                  />
                  {p.condensation_image && (
                    <img
                      src={p.condensation_image}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out group-hover:scale-105"
                      data-testid={`condensation-${i}`}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D110D] via-transparent to-transparent opacity-80" />
                  <span className="absolute top-4 left-4 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#E8E2D2]/70 border border-white/20 px-3 py-1 backdrop-blur-sm">
                    {p.style}
                  </span>
                  {p.is_bundle ? (
                    <span className="absolute top-4 right-4 font-mono-accent text-[10px] uppercase tracking-[0.15em] bg-[#D47738] text-[#0D110D] px-3 py-1 font-semibold" data-testid={`bundle-badge-${i}`}>
                      {p.can_count} lattine
                    </span>
                  ) : (
                    <span className="absolute top-4 right-4 font-mono-accent text-[10px] text-[#D47738]">
                      {p.abv}% ABV
                    </span>
                  )}
                  {p.stock <= 0 && (
                    <span className="absolute bottom-4 left-4 bg-[#9E3C3C] text-[#E8E2D2] font-mono-accent text-[10px] uppercase tracking-[0.2em] px-3 py-1.5" data-testid={`soldout-badge-${i}`}>
                      Esaurito
                    </span>
                  )}
                </Link>

                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <Link to={`/birra/${p.id}`} className="font-serif-display text-3xl text-[#E8E2D2] leading-none hover:text-[#D47738] transition-colors">
                      {p.name}
                    </Link>
                    <span className="font-mono-accent text-xl text-[#D47738] whitespace-nowrap">
                      € {p.price.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-[#E8E2D2]/50 leading-relaxed mb-6 min-h-[60px]">
                    {p.description}
                  </p>
                  {!cartEnabled ? null : p.stock <= 0 ? (
                    <div
                      className="w-full text-center border border-white/10 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2]/40"
                      data-testid={`soldout-${i}`}
                    >
                      Esaurito
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAdd(p)}
                      className="w-full flex items-center justify-center gap-2 border border-white/20 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2] hover:bg-[#D47738] hover:text-[#0D110D] hover:border-[#D47738] transition-colors duration-300"
                      data-testid={`add-to-cart-${i}`}
                    >
                      <Plus className="w-4 h-4" strokeWidth={2} />
                      Aggiungi
                    </button>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
